<template>
  <div class="dashboard">
    <mainmenu selected="dashboard"></mainmenu>
    <div class="container-fluid dashboard-shell">
      <div class="dashboard-control-card">
        <div class="dashboard-header">
          <div>
            <h1>{{$t('dashboard.page_title')}}</h1>
            <div class="dashboard-summary">
              {{$t('dashboard.available_offers')}} <strong>{{PaginationInfo.RowCount}}</strong>
            </div>
          </div>
        </div>

        <div class="filters-bar">
          <span class="filters-label">{{$t('filters')}}</span>
          <div class="filters-grid" :class="{'filters-grid-admin': $rights.is_admin()}">
            <label class="filter-field filter-article">
              <span>{{$t('dashboard.article_code')}}</span>
              <el-autocomplete
                size="small"
                class="filter-control"
                v-model="Filters.ArticleCode"
                :fetch-suggestions="queryCodes"
                :placeholder="$t('dashboard.search_article')"
                clearable>
              </el-autocomplete>
            </label>

            <label class="filter-field filter-quantity">
              <span>{{$t('dashboard.quantity_range')}}</span>
              <div class="quantity-range">
                <el-input
                  size="small"
                  type="number"
                  v-model="Filters.MinQuantity"
                  :placeholder="$t('dashboard.min_quantity')">
                </el-input>
                <span class="range-divider">-</span>
                <el-input
                  size="small"
                  type="number"
                  v-model="Filters.MaxQuantity"
                  :placeholder="$t('dashboard.max_quantity')">
                </el-input>
              </div>
            </label>

            <label class="filter-field">
              <span>{{$t('dashboard.category')}}</span>
              <el-select size="small" class="filter-control" v-model="Filters.IdCategory" filterable>
                <el-option :label="$t('all')" :value="-1" :key="-1"></el-option>
                <el-option v-for="c in Categories" :label="c.Name" :value="c.Id" :key="c.Id"></el-option>
              </el-select>
            </label>
            
            <label class="filter-field">
              <span>{{$t('dashboard.factory_code')}}</span>
              <el-select size="small" class="filter-control" v-model="Filters.IdFactory" filterable>
                <el-option :label="$t('all')" :value="-1" :key="-1"></el-option>
                <el-option 
                        v-for="f in Factories"
                        :label="f.ShortCode"
                        :key="f.Id"
                        :value="f.Id"></el-option>
              </el-select>
            </label>

            <label class="filter-field filter-preview" v-if="$rights.is_admin()">
              <span>{{$t('dashboard.preview_seller')}}</span>
              <el-select
                size="small"
                class="filter-control"
                v-model="Filters.PreviewSellerId"
                :placeholder="$t('dashboard.preview_seller_placeholder')"
                filterable
                clearable>
                <el-option
                  v-for="seller in PreviewSellers"
                  :label="seller.Name"
                  :key="seller.Id"
                  :value="seller.Id">
                </el-option>
              </el-select>
            </label>

            <div class="filter-actions">
              <el-button size="small" type="primary" icon="el-icon-search" @click="applyFilters">{{$t('filter')}}</el-button>
              <el-button size="small" icon="el-icon-refresh-left" @click="resetFilters">{{$t('reset')}}</el-button>
            </div>
          </div>
        </div>
      </div>

      <div class="dashboard-table-card">
        <div class="table-card-header">
          <div>
            <div class="table-title">{{$t('dashboard.stock_offers')}}</div>
            <div class="table-subtitle">{{$t('dashboard.sorted_by_id')}}</div>
          </div>
          <span class="table-total top-total">{{$t('dashboard.total')}} {{PaginationInfo.RowCount}}</span>
        </div>

       <el-table
          class="dashboard-table"
          stripe
          :data="Results"
          style="width: 100%"
          v-loading="tableLoadingVisible"
          :empty-text="$t('dashboard.empty_results')"
          :default-sort="{prop: 'Id', order: 'descending'}"
          :row-class-name="offerRowClass"
          @sort-change="on_sort">

          <el-table-column sortable="custom" prop="Id" :label="$t('dashboard.unique_id')" width="72"></el-table-column>
          <el-table-column sortable="custom" prop="FirstUploadDate" :label="$t('dashboard.created_date')" width="172">
            <template slot-scope="scope">
              <span class="date-chip" v-if="scope.row.FirstUploadDate" :title="formatDashboardDate(scope.row.FirstUploadDate, 'utc')">
                <span class="date-main">{{formatDashboardDate(scope.row.FirstUploadDate, 'date')}}</span>
                <span class="date-time">{{formatDashboardDate(scope.row.FirstUploadDate, 'time')}}</span>
              </span>
            </template>
          </el-table-column>
          <el-table-column sortable="custom" prop="FactoryCode" :label="$t('dashboard.factory_code')" width="120"></el-table-column>
          <el-table-column sortable="custom" prop="SellerName" :label="$t('dashboard.seller_name')" min-width="155" show-overflow-tooltip></el-table-column>
          
          <el-table-column :label="$t('dashboard.article_info')" min-width="460">
            <template slot-scope="scope">
                <div class="article-main"><strong>{{scope.row.ArticleNumber}}</strong> - {{scope.row.ProductName}}</div>
                <div class="article-category">{{scope.row.ProductCategory}}</div>
                <p class="article-description">{{scope.row.ProductDescription}}</p>
            </template>
          </el-table-column>

          <el-table-column sortable="custom" prop="Quantity" :label="$t('dashboard.quantity')" width="105" align="right">
            <template slot-scope="scope">
              <span class="number-cell">{{formatNumber(scope.row.Quantity)}}</span>
            </template>
          </el-table-column>
          <el-table-column sortable="custom" prop="Price" :label="$t('dashboard.price')" width="105" align="right">
            <template slot-scope="scope">
                <span class="number-cell">{{scope.row.Price|format_money}} &euro;</span>
            </template>
          </el-table-column>
          <el-table-column fixed="right" :label="$t('dashboard.operations')" width="120">
            <template slot-scope="scope">
              <div class="order-action">
                <el-tooltip
                  v-if="isOwnOffer(scope.row)"
                  :content="$t('dashboard.own_offer_hint')"
                  placement="left"
                  effect="dark">
                  <span class="order-disabled-wrap">
                    <el-button class="order-button order-button-disabled" type="text" size="mini" icon="el-icon-shopping-cart-2" disabled>{{$t('dashboard.own_offer')}}</el-button>
                  </span>
                </el-tooltip>
                <el-button v-else class="order-button" @click="show_buy_dialog(scope.row)" type="text" size="mini" icon="el-icon-shopping-cart-2">{{$t('dashboard.order')}}</el-button>
              </div>
            </template>
          </el-table-column>
      </el-table>

        <div class="table-footer">
          <span class="table-total">{{$t('dashboard.total')}} {{PaginationInfo.RowCount}}</span>
          <el-pagination
              small
              background
              @size-change        = "on_page_size_change"
              @current-change     = "refresh_info"
              :page-sizes         = "PaginationInfo.PageSizes"
              :page-size.sync     = "PaginationInfo.PerPage"
              :current-page.sync  = "PaginationInfo.Page"
              :total              = "PaginationInfo.RowCount"
              layout              = "sizes, prev, pager, next">
          </el-pagination>
        </div>
      </div>

      <buy-dialog ref="buyDialog" @neworder="refresh_info"></buy-dialog>

    </div>
  </div>
</template>

<script>
import MainMenu from "./../widgets/MainMenu";
import settings from "./../backend/LocalSettings";
import BuyDialog from "./../views/dialogs/BuyDialog"
import { EventBus } from "./../backend/EventBus.js";
import { intlLocaleForLanguage } from "./../utils/languages";

export default {
  name: "dashboard",
  components: {
    "mainmenu": MainMenu,
    "buy-dialog": BuyDialog
  },
  data () {
    return { 
      tableLoadingVisible: false,
      Results: [],
      Filters: { ArticleCode: "", MinQuantity:"", MaxQuantity:"", IdCategory:-1, IdFactory:-1, PreviewSellerId:"", OfferId:"" },
      OrderBy: { Column:"Id", Order:"DESC" },
      PaginationInfo: { Page: 1, PerPage: 20, RowCount: 0, PageSizes: [20, 50, 100] },
      Categories: [],
      Factories:[],
      PreviewSellers: [],
      DirectOfferId: ""
    }
  },
  methods: {
    applyDeepLink: function() {
      var factoryId = parseInt(this.$route.query.factory_id);
      if(!isNaN(factoryId) && factoryId > 0) {
        settings.set_active_factory_id(factoryId);
      }

      var offerId = parseInt(this.$route.query.offer_id);
      if(!isNaN(offerId) && offerId > 0) {
        this.DirectOfferId = String(offerId);
        this.Filters.OfferId = String(offerId);
      }
    },
    applyFilters: function(){
      this.PaginationInfo.Page = 1;
      this.refresh_info();
    },
    resetFilters: function(){
      this.DirectOfferId = "";
      this.Filters = { ArticleCode: "", MinQuantity:"", MaxQuantity:"", IdCategory:-1, IdFactory:-1, PreviewSellerId:"", OfferId:"" };
      this.applyFilters();
    },
    show_buy_dialog: function(item){
      if(!this.canOrder(item)) return;
      this.$refs.buyDialog.show_me(item);
    },
    isOwnOffer: function(item) {
      return item && (item.IsOwnOffer === true || item.IsOwnOffer === 1 || item.IsOwnOffer === "1");
    },
    canOrder: function(item) {
      if(!item || this.isOwnOffer(item)) return false;
      if(item.CanOrder === undefined || item.CanOrder === null) return true;
      return !(item.CanOrder === false || item.CanOrder === 0 || item.CanOrder === "0");
    },
    on_sort: function(data){
      if( data.prop != null ){
        this.OrderBy.Column = data.prop;
        this.OrderBy.Order  = data.order == "ascending" ? "ASC" : "DESC";
      }
      else
      {
        this.OrderBy.Column = "Id";
        this.OrderBy.Order  = "DESC";
      }
      this.PaginationInfo.Page = 1;
      this.refresh_info();
    },
    formatDashboardDate: function(value, part) {
      if(!value || value === "0000-00-00 00:00:00") return "-";
      var parts = String(value).split(/[- :]/);
      if(parts.length < 5) return value;

      var pad = function(part) {
        return String(part).padStart(2, "0");
      };
      var dateValue = new Date(Date.UTC(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2]),
        Number(parts[3] || 0),
        Number(parts[4] || 0),
        Number(parts[5] || 0)
      ));
      if(isNaN(dateValue.getTime())) return value;

      var date = pad(dateValue.getDate()) + "." + pad(dateValue.getMonth() + 1) + "." + dateValue.getFullYear();
      var time = pad(dateValue.getHours()) + ":" + pad(dateValue.getMinutes());
      if(part === "utc") return "UTC: " + value;
      if(part === "date") return date;
      if(part === "time") return time;
      return date + " " + time;
    },
    formatNumber: function(value) {
      if(value === null || value === undefined || value === "") return "";
      return new Intl.NumberFormat(intlLocaleForLanguage(this.$i18n && this.$i18n.locale)).format(Number(value));
    },
    on_page_size_change: function() {
      this.PaginationInfo.Page = 1;
      this.refresh_info();
    },
    get_info: async function() {
      this.tableLoadingVisible = true;
      try {
        var response = await this.$http.post('dashboard/get_info',  {} );
        if( settings.verify_response(response, this.$rights)) 
        {
          this.Categories = response.body.Categories;
          this.Factories  = response.body.Factories;
          this.PreviewSellers = response.body.PreviewSellers || [];
          this.refresh_info();
        } else {
          this.$router.push("/");
        }
      } catch(_err) {
        this.tableLoadingVisible = false;
        this.$message.error("Could not load dashboard filters. Please try again.");
      }
    },
    refresh_info: function() {
      this.tableLoadingVisible = true;
      this.$http
          .post('dashboard/refresh_info',  { OrderBy: this.OrderBy, Filters: this.Filters, PaginationInfo: this.PaginationInfo } )
          .then(function (response) {
            this.tableLoadingVisible = false;
            if( settings.verify_response(response, this.$rights)) {
              this.Results = response.body.Results;
              this.PaginationInfo = response.body.PaginationInfo;
            } else {
              this.$router.push("/");
            }
          })
          .catch(function() {
            this.tableLoadingVisible = false;
            this.$message.error("Could not load dashboard data. Please try again.");
          }); 
    },
    queryCodes: function(queryString, cb){
      this.$http
          .post('products/search_code',  { query: queryString } )
          .then(function (response) {
            this.tableLoadingVisible = false;
            if( settings.verify_response(response, this.$rights)) {
              cb(response.body.Results);
            } else {
              this.$router.push("/");
            }
          })
          .catch(function() {
            cb([]);
          }); 
    },
    logout: function() {
      settings.logout();
      this.$router.push("/");
    },
    on_factory_context_change: function() {
      this.PaginationInfo.Page = 1;
      this.refresh_info();
    },
    offerRowClass: function({ row }) {
      return this.DirectOfferId && row && String(row.Id) === String(this.DirectOfferId) ? "direct-offer-row" : "";
    }
  },
  created: function() {
    this.applyDeepLink();
  },
  mounted: function() {
    this.get_info();
    EventBus.$on('on_factory_context_change', this.on_factory_context_change);
  },
  beforeDestroy: function() {
    EventBus.$off('on_factory_context_change', this.on_factory_context_change);
  }
};
</script>

<style lang="less" scoped>
.dashboard-shell {
  padding: 14px 10px 28px;
}

.dashboard-control-card {
  margin-bottom: 14px;
  padding: 16px 18px 15px;
  border: 1px solid rgba(26, 88, 111, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 26px rgba(25, 80, 99, 0.13);
}

.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 13px;
  border-bottom: 1px solid #e8eef2;

  h1 {
    margin: 0;
    color: #152b38;
    font-size: 31px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 36px;
  }
}

.dashboard-summary {
  margin-top: 1px;
  color: rgba(21, 43, 56, 0.72);
  font-size: 13px;
  font-weight: 600;
}

.filters-bar {
  display: grid;
  grid-template-columns: 54px 1fr;
  align-items: center;
  gap: 14px;
}

.filters-label {
  color: #2e4a5a;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.filters-grid {
  display: grid;
  grid-template-columns: minmax(230px, 1.15fr) minmax(240px, 0.95fr) minmax(180px, 0.9fr) minmax(160px, 0.75fr) auto;
  align-items: end;
  gap: 10px;
}

.filters-grid-admin {
  grid-template-columns: minmax(200px, 1fr) minmax(210px, 0.9fr) minmax(165px, 0.75fr) minmax(140px, 0.65fr) minmax(220px, 1fr) auto;
}

.filter-field {
  display: block;
  margin: 0;
  min-width: 0;

  > span {
    display: block;
    margin-bottom: 5px;
    color: #556b78;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
  }
}

.filter-control {
  width: 100%;

  /deep/ .el-input__inner {
    border-color: #d7e0e7;
    color: #324a59;
    font-weight: 600;
  }
}

.quantity-range {
  display: grid;
  grid-template-columns: minmax(90px, 1fr) auto minmax(110px, 1fr);
  align-items: center;
  gap: 8px;

  /deep/ .el-input {
    width: 100%;
  }

  /deep/ .el-input__inner {
    border-color: #d7e0e7;
    color: #324a59;
    font-weight: 600;
    text-align: right;
  }
}

.range-divider {
  color: #74838c;
  font-weight: 700;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 1px;
  white-space: nowrap;
}

.dashboard-table-card {
  overflow: hidden;
  border: 1px solid rgba(26, 88, 111, 0.12);
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 12px 26px rgba(32, 85, 103, 0.12);
}

.table-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 54px;
  padding: 12px 18px;
  border-bottom: 1px solid #e6edf2;
  background: #fbfdfe;
}

.table-title {
  color: #223947;
  font-size: 15px;
  font-weight: 800;
  line-height: 20px;
}

.table-subtitle {
  color: #6d7f89;
  font-size: 12px;
  font-weight: 600;
}

.dashboard-table {
  background: #fff;
  border-top: 0;
}

.dashboard-table /deep/ th {
  padding: 10px 0;
  background: #f4f8fa;
  color: #526875;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.dashboard-table /deep/ td {
  padding: 7px 0;
  border-bottom-color: #e7edf2;
  color: #405461;
}

.dashboard-table /deep/ .cell {
  line-height: 21px;
}

.dashboard-table /deep/ .el-table__row:hover > td {
  background: #f4fbfd;
}

.dashboard-table /deep/ .direct-offer-row > td {
  background: #fff7e6 !important;
}

.date-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 7px;
  max-width: 100%;
  white-space: nowrap;
  color: #314d5d;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.date-main {
  font-weight: 800;
}

.date-time {
  color: #6a7b84;
  font-size: 12px;
  font-weight: 700;
}

.article-main {
  color: #314754;
  line-height: 19px;
}

.article-category {
  margin-top: 2px;
  color: #526976;
  font-style: italic;
  line-height: 18px;
}

.article-description {
  margin: 4px 0 0;
  color: #64727c;
  font-size: 12px;
  line-height: 17px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  min-width: 48px;
  justify-content: center;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
}

.status-used {
  background: #eef4f8;
  color: #426274;
}

.status-new {
  background: #e9f7ef;
  color: #2d7d4b;
}

.number-cell {
  color: #344d5d;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.order-button {
  padding: 0;
  color: #177f98;
  font-weight: 700;

  &:hover,
  &:focus {
    color: #0d6175;
  }
}

.order-disabled-wrap {
  display: inline-flex;
}

.order-button-disabled,
.order-button-disabled.is-disabled,
.order-button-disabled.is-disabled:hover,
.order-button-disabled.is-disabled:focus {
  color: #84939c;
  cursor: not-allowed;
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  min-height: 50px;
  padding: 9px 18px;
  border-top: 1px solid #e6edf2;
  background: #fbfdfe;
}

.table-total {
  color: #617482;
  font-size: 13px;
  font-weight: 600;
}

.top-total {
  padding: 4px 9px;
  border-radius: 4px;
  background: #edf5f8;
  color: #2e6275;
}

@media (max-width: 1180px) {
  .filters-bar {
    grid-template-columns: 1fr;
    gap: 9px;
  }

  .filters-grid {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  .filter-actions {
    grid-column: 1 / -1;
  }
}

@media (max-width: 760px) {
  .dashboard-header h1 {
    font-size: 32px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
  }

  .table-footer {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
